// Portfolio Administration v1.0 PR 4b — HTTP transport for the
// Defaults & Workout + Payment Tracking surfaces. Hooks in
// @/hooks/portfolioAdmin/usePr4b wrap these with TanStack Query.

import { api } from "../client";
import type {
  ActivateDACARequest,
  AppendWorkoutEventRequest,
  CollateralRecoveryDto,
  CreateEnforcerEngagementRequest,
  CurePeriodDto,
  DACAEventDto,
  DefaultCaseDto,
  DefaultCaseStateMachineResponse,
  EnforcerEngagementDto,
  ExecuteForbearanceRequest,
  ExtendCurePeriodRequest,
  ForbearanceAgreementDto,
  InitiateLegalActionRequest,
  LegalActionDto,
  LoanTapeEntryDto,
  CreateLocalEnforcerRequest,
  LocalEnforcerDetailDto,
  LocalEnforcerDto,
  LocalEnforcerPerformanceReviewRequest,
  MarkCuredRequest,
  QualifyLocalEnforcerRequest,
  UpdateLocalEnforcerStatusRequest,
  OpenDefaultCaseRequest,
  ProposeForbearanceRequest,
  ProposeRemediationPlanRequest,
  RecordWorkoutCostRequest,
  RecordWorkoutRecoveryRequest,
  RemediationPlanDto,
  SegmentBalanceDto,
  SegmentLedgerEntryDto,
  SetResolutionRequest,
  TransitionDefaultCaseRequest,
  WorkoutCostEntryDto,
  WorkoutEventDto,
  WorkoutRecoveryEntryDto,
  WorkoutRecoveryTotalsDto,
} from "./pr4bTypes";

// ---- default cases (§7.8) ----------------------------------------------

export const defaultCasesApi = {
  listForLoan: (loanId: string) =>
    api.get<DefaultCaseDto[]>(`/portfolio-admin/loans/${loanId}/default-cases`),
  get: (caseId: string) =>
    api.get<DefaultCaseDto>(`/portfolio-admin/default-cases/${caseId}`),
  open: (loanId: string, body: OpenDefaultCaseRequest) =>
    api.post<DefaultCaseDto>(
      `/portfolio-admin/loans/${loanId}/default-cases`,
      body,
    ),
  transition: (caseId: string, body: TransitionDefaultCaseRequest) =>
    api.patch<DefaultCaseDto>(
      `/portfolio-admin/default-cases/${caseId}/transition`,
      body,
    ),
  setResolution: (caseId: string, body: SetResolutionRequest) =>
    api.patch<DefaultCaseDto>(
      `/portfolio-admin/default-cases/${caseId}/resolve`,
      body,
    ),
  getStateMachine: (caseId: string) =>
    api.get<DefaultCaseStateMachineResponse>(
      `/portfolio-admin/default-cases/${caseId}/state-machine`,
    ),
};

// ---- cure periods ------------------------------------------------------

export const curePeriodsApi = {
  listForCase: (caseId: string) =>
    // PR 2b didn't ship a list-by-case endpoint; the most recent cure
    // period is derived from the DefaultCase aggregate. We expose this
    // helper for forward-compat, and fall back to the singleton GET
    // below when the case-level list isn't yet wired.
    api.get<CurePeriodDto[]>(`/portfolio-admin/default-cases/${caseId}/cure-periods`),
  extend: (id: string, body: ExtendCurePeriodRequest) =>
    api.patch<CurePeriodDto>(
      `/portfolio-admin/cure-periods/${id}/extend`,
      body,
    ),
  markCured: (id: string, body: MarkCuredRequest) =>
    api.patch<CurePeriodDto>(
      `/portfolio-admin/cure-periods/${id}/mark-cured`,
      body,
    ),
};

// ---- remediation plans -------------------------------------------------

export const remediationPlansApi = {
  listForCase: (caseId: string) =>
    api.get<RemediationPlanDto[]>(
      `/portfolio-admin/default-cases/${caseId}/remediation-plans`,
    ),
  propose: (caseId: string, body: ProposeRemediationPlanRequest) =>
    api.post<RemediationPlanDto>(
      `/portfolio-admin/default-cases/${caseId}/remediation-plans`,
      body,
    ),
  approve: (id: string) =>
    api.patch<RemediationPlanDto>(
      `/portfolio-admin/remediation-plans/${id}/approve-phoenix`,
    ),
  activate: (id: string) =>
    api.patch<RemediationPlanDto>(
      `/portfolio-admin/remediation-plans/${id}/activate`,
    ),
};

// ---- DACA events -------------------------------------------------------

export const dacaApi = {
  listForCase: (caseId: string) =>
    api.get<DACAEventDto[]>(`/portfolio-admin/default-cases/${caseId}/daca`),
  activate: (caseId: string, body: ActivateDACARequest) =>
    api.post<DACAEventDto>(
      `/portfolio-admin/default-cases/${caseId}/daca`,
      body,
    ),
};

// ---- legal actions -----------------------------------------------------

export const legalActionsApi = {
  listForCase: (caseId: string) =>
    api.get<LegalActionDto[]>(
      `/portfolio-admin/default-cases/${caseId}/legal-actions`,
    ),
  initiate: (caseId: string, body: InitiateLegalActionRequest) =>
    api.post<LegalActionDto>(
      `/portfolio-admin/default-cases/${caseId}/legal-actions`,
      body,
    ),
};

// ---- collateral recoveries (§9-610 / §9-621) ---------------------------

export const collateralRecoveriesApi = {
  listForCase: (caseId: string) =>
    api.get<CollateralRecoveryDto[]>(
      `/portfolio-admin/default-cases/${caseId}/collateral-recoveries`,
    ),
};

// ---- workout events ----------------------------------------------------

export const workoutEventsApi = {
  listForCase: (
    caseId: string,
    params?: { category?: string; from?: string; to?: string },
  ) =>
    api.get<WorkoutEventDto[]>(
      `/portfolio-admin/default-cases/${caseId}/events`,
      params,
    ),
  append: (caseId: string, body: AppendWorkoutEventRequest) =>
    api.post<WorkoutEventDto>(
      `/portfolio-admin/default-cases/${caseId}/events`,
      body,
    ),
};

// ---- forbearance agreements --------------------------------------------

export const forbearanceApi = {
  listForCase: (caseId: string) =>
    api.get<ForbearanceAgreementDto[]>(
      `/portfolio-admin/default-cases/${caseId}/forbearance`,
    ),
  propose: (caseId: string, body: ProposeForbearanceRequest) =>
    api.post<ForbearanceAgreementDto>(
      `/portfolio-admin/default-cases/${caseId}/forbearance`,
      body,
    ),
  execute: (id: string, body: ExecuteForbearanceRequest) =>
    api.patch<ForbearanceAgreementDto>(
      `/portfolio-admin/forbearance/${id}/execute`,
      body,
    ),
};

// ---- enforcer engagements ---------------------------------------------

export const enforcerEngagementsApi = {
  createForEnforcer: (
    enforcerId: string,
    body: CreateEnforcerEngagementRequest,
  ) =>
    api.post<EnforcerEngagementDto>(
      `/portfolio-admin/local-enforcers/${enforcerId}/engagements`,
      body,
    ),
  listForEnforcer: (enforcerId: string) =>
    api.get<EnforcerEngagementDto[]>(
      `/portfolio-admin/local-enforcers/${enforcerId}/engagements`,
    ),
  // Defaults & Workout rebuild — per-case list backing the enforcer
  // side-rail card (the old card was a stub: no case-scoped endpoint).
  listForCase: (caseId: string) =>
    api.get<EnforcerEngagementDto[]>(
      `/portfolio-admin/default-cases/${caseId}/engagements`,
    ),
};

export const suggestedEnforcersApi = {
  forLoan: (loanId: string) =>
    api.get<LocalEnforcerDto[]>(
      `/portfolio-admin/loans/${loanId}/suggested-enforcers`,
    ),
};

// ---- local enforcers (registry) ---------------------------------------
// Portfolio Administration v1.0 PR 5 — full registry CRUD for the
// /portfolio-admin/local-enforcers list + detail pages.

export const localEnforcersApi = {
  search: (params?: {
    jurisdiction?: string;
    type?: string;
    status?: string;
    search?: string;
    types?: string[];
    statuses?: string[];
    jurisdictions?: string[];
  }) =>
    api.get<LocalEnforcerDto[]>(`/portfolio-admin/local-enforcers`, params),
  get: (enforcerId: string) =>
    api.get<LocalEnforcerDto>(`/portfolio-admin/local-enforcers/${enforcerId}`),
  getDetail: (enforcerId: string) =>
    api.get<LocalEnforcerDetailDto>(
      `/portfolio-admin/local-enforcers/${enforcerId}/detail`,
    ),
  create: (body: CreateLocalEnforcerRequest) =>
    api.post<LocalEnforcerDto>(`/portfolio-admin/local-enforcers`, body),
  updateStatus: (enforcerId: string, body: UpdateLocalEnforcerStatusRequest) =>
    api.patch<LocalEnforcerDto>(
      `/portfolio-admin/local-enforcers/${enforcerId}/status`,
      body,
    ),
  qualify: (enforcerId: string, body: QualifyLocalEnforcerRequest) =>
    api.patch<LocalEnforcerDto>(
      `/portfolio-admin/local-enforcers/${enforcerId}/qualify`,
      body,
    ),
  recordPerformanceReview: (
    enforcerId: string,
    body: LocalEnforcerPerformanceReviewRequest,
  ) =>
    api.patch<LocalEnforcerDto>(
      `/portfolio-admin/local-enforcers/${enforcerId}/performance-review`,
      body,
    ),
};

// ---- workout costs / recoveries ---------------------------------------

export const workoutCostsApi = {
  listForCase: (caseId: string, params?: { category?: string }) =>
    api.get<WorkoutCostEntryDto[]>(
      `/portfolio-admin/default-cases/${caseId}/costs`,
      params,
    ),
  record: (caseId: string, body: RecordWorkoutCostRequest) =>
    api.post<WorkoutCostEntryDto>(
      `/portfolio-admin/default-cases/${caseId}/costs`,
      body,
    ),
  approve: (id: string) =>
    api.patch<WorkoutCostEntryDto>(`/portfolio-admin/costs/${id}/approve`),
};

export const workoutRecoveriesApi = {
  listForCase: (caseId: string) =>
    api.get<WorkoutRecoveryEntryDto[]>(
      `/portfolio-admin/default-cases/${caseId}/recoveries`,
    ),
  record: (caseId: string, body: RecordWorkoutRecoveryRequest) =>
    api.post<WorkoutRecoveryEntryDto>(
      `/portfolio-admin/default-cases/${caseId}/recoveries`,
      body,
    ),
  totals: (caseId: string) =>
    api.get<WorkoutRecoveryTotalsDto>(
      `/portfolio-admin/default-cases/${caseId}/recoveries/totals`,
    ),
};

// ---- loan tape (§7.9) --------------------------------------------------

export const loanTapeApi = {
  listForLoan: (loanId: string, params?: { limit?: number }) =>
    api.get<LoanTapeEntryDto[]>(
      `/portfolio-admin/loans/${loanId}/loan-tape`,
      params,
    ),
  get: (entryId: string) =>
    api.get<LoanTapeEntryDto>(`/portfolio-admin/loan-tape/${entryId}`),
  // SP-6 — the tape WRITE methods (enter / setVariance / lock / unlock) were
  // removed: the legacy tape is read-only (SP-1 made the backend throw) and the
  // loan tab runs on the v3 servicing ledger. Reads (listForLoan / get) stay.
};

// SP-6 — the manual waterfall-run client (execute / preview / approve / reverse
// + GET run reads) was RETIRED. Run history is served via pr6Client
// (RunHistorySidebar) and the portfolio-metrics financial card.

// ---- segment ledger ----------------------------------------------------

export const segmentLedgerApi = {
  balances: (loanId: string) =>
    api.get<SegmentBalanceDto[]>(
      `/portfolio-admin/loans/${loanId}/segments/balance`,
    ),
  ledgerForSegment: (loanId: string, n: number, params?: { limit?: number }) =>
    api.get<SegmentLedgerEntryDto[]>(
      `/portfolio-admin/loans/${loanId}/segments/${n}/ledger`,
      params,
    ),
};
